import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { Terrain, SurfaceObstacle } from '../../surface';
import { createMatteMaterial } from '../../../visual';
import type { FloraSpec } from '../BiomeProfile';
import { buildArchetype, estimatedRadius } from './archetypes';

export interface FloraResult {
  readonly group: THREE.Group;
  readonly obstacles: readonly SurfaceObstacle[];
}

export interface FloraScatterOptions {
  readonly spread: number;
  readonly excludeRadius?: number;
  readonly minAltitude?: number;
  readonly retries?: number;
  readonly innerRingFraction?: number;
  readonly innerRingMax?: number;
}

/**
 * Instance-rendered flora scatter. The previous per-plant `Group` approach
 * generated ~12K draw calls per planet (2400 plants × ~5 sub-meshes), which
 * stalled scene transitions. The new pipeline collapses that to ~3 draw
 * calls per spec by:
 *
 *   1. Building a small bag of `VARIANTS_PER_SPEC` template plants per
 *      species (organic seeds bake into the variant's vertices once).
 *   2. Walking each template, baking its sub-meshes' local transforms
 *      into per-material merged geometries.
 *   3. Allocating instance matrices for the random positions/yaws.
 *   4. Building one `InstancedMesh` per (variant × material) tuple.
 *
 * Per-instance scale jitter (0.88-1.12) breaks the variant-clone
 * uniformity without forcing per-plant geometry. Counts up to a few
 * thousand are now effectively free GPU-side.
 */

const VARIANTS_PER_SPEC = 5;

const _tmpEuler = new THREE.Euler();
const _tmpQuat = new THREE.Quaternion();
const _tmpScale = new THREE.Vector3();
const _tmpPos = new THREE.Vector3();

interface BakedVariant {
  // One merged geometry per unique material in the variant. Materials
  // are owned by the caller (created once per spec) so they're not
  // duplicated here.
  readonly batches: ReadonlyArray<{
    readonly geometry: THREE.BufferGeometry;
    readonly material: THREE.Material;
  }>;
}

interface PlantTransform {
  readonly variantIdx: number;
  readonly matrix: THREE.Matrix4;
}

export function scatterFlora(
  terrain: Terrain,
  specs: readonly FloraSpec[],
  options: FloraScatterOptions
): FloraResult {
  const group = new THREE.Group();
  group.name = 'biome-flora';
  const obstacles: SurfaceObstacle[] = [];
  const exclude = options.excludeRadius ?? 0;
  const excludeSq = exclude * exclude;
  const minY = options.minAltitude ?? -Infinity;
  const retries = options.retries ?? 10;
  const innerRingFraction = options.innerRingFraction ?? 0;
  const innerRingMax = options.innerRingMax ?? 130;

  for (const spec of specs) {
    // Materials shared across all variants and instances of this spec.
    const bodyMat = createMatteMaterial({
      color: spec.tint,
      roughness: 0.78,
      flatShading: false
    });
    const accentMat = createMatteMaterial({
      color: spec.accent ?? spec.tint,
      roughness: 0.62,
      flatShading: false
    });
    let emissiveMat: THREE.MeshStandardMaterial | null = null;
    if (spec.emissive !== undefined) {
      emissiveMat = createMatteMaterial({
        color: spec.emissive,
        roughness: 0.55,
        emissive: spec.emissive,
        emissiveIntensity: spec.emissiveStrength ?? 0.5,
        flatShading: false
      });
      emissiveMat.toneMapped = false;
    }
    const ctx = { bodyMat, accentMat, emissiveMat };

    // Build M template variants — each one is a unique organic shape;
    // every plant of this spec maps to one of these variants.
    const variants: BakedVariant[] = [];
    for (let i = 0; i < VARIANTS_PER_SPEC; i++) {
      // Sample scale evenly across the spec's range so the bag covers
      // small, medium and large silhouettes.
      const t = (i + 0.5) / VARIANTS_PER_SPEC;
      const variantScale = spec.minScale + t * (spec.maxScale - spec.minScale);
      const template = buildArchetype(spec.archetype, variantScale, ctx);
      variants.push(bakeVariant(template));
    }

    const minDistanceFromCenter = spec.minDistanceFromCenter ?? 0;
    const minDistSq = minDistanceFromCenter * minDistanceFromCenter;
    const innerTarget = Math.floor(spec.count * innerRingFraction);
    const transforms: PlantTransform[] = [];

    let placed = 0;
    let innerPlaced = 0;
    let attempts = 0;
    const maxAttempts = spec.count * retries;

    while (placed < spec.count && attempts++ < maxAttempts) {
      const inInner = innerPlaced < innerTarget;
      const angle = Math.random() * Math.PI * 2;
      let r: number;
      if (inInner) {
        const minR = Math.max(exclude, 4);
        const maxR = innerRingMax;
        r = Math.sqrt(minR * minR + Math.random() * (maxR * maxR - minR * minR));
      } else {
        r = Math.sqrt(Math.random()) * options.spread;
      }
      const x = Math.cos(angle) * r;
      const z = Math.sin(angle) * r;
      const distSq = x * x + z * z;
      if (distSq < excludeSq) continue;
      if (distSq < minDistSq) continue;

      const groundY = terrain.getHeightAt(x, z);
      if (groundY < minY) continue;

      if (spec.altitudeBias !== undefined && spec.altitudeBias !== 0) {
        const sample = terrain.getHeightAt(x + 6, z + 6);
        const slope = sample - groundY;
        if (spec.altitudeBias < 0 && slope > 1.2) continue;
        if (spec.altitudeBias > 0 && slope < -0.6) continue;
      }

      const cluster = Math.random() < (spec.clusterChance ?? 0.18);
      const copies = cluster ? 1 + Math.floor(Math.random() * 3) : 1;

      for (let c = 0; c < copies && placed < spec.count; c++) {
        const dx = cluster ? (Math.random() - 0.5) * 1.8 : 0;
        const dz = cluster ? (Math.random() - 0.5) * 1.8 : 0;
        const px = x + dx;
        const pz = z + dz;
        const py = terrain.getHeightAt(px, pz);
        if (py < minY) continue;

        const variantIdx = Math.floor(Math.random() * VARIANTS_PER_SPEC);
        // Per-instance scale jitter so variant clones don't read as
        // identical copies.
        const instanceScale = 0.88 + Math.random() * 0.24;
        const yaw = Math.random() * Math.PI * 2;
        const tilt = (Math.random() - 0.5) * 0.18;
        const pitch = (Math.random() - 0.5) * 0.18;

        _tmpEuler.set(pitch, yaw, tilt);
        _tmpQuat.setFromEuler(_tmpEuler);
        _tmpPos.set(px, py, pz);
        _tmpScale.set(instanceScale, instanceScale, instanceScale);
        const matrix = new THREE.Matrix4().compose(_tmpPos, _tmpQuat, _tmpScale);

        transforms.push({ variantIdx, matrix });

        // Variant scale baseline so obstacle radius/height reflect the
        // actual visible size of this instance.
        const variantBaseline =
          spec.minScale + ((variantIdx + 0.5) / VARIANTS_PER_SPEC) *
            (spec.maxScale - spec.minScale);
        const finalScale = variantBaseline * instanceScale;
        const radius = estimatedRadius(spec, finalScale);

        if (spec.archetype === 'vesicle-tree') {
          obstacles.push({
            position: new THREE.Vector3(px, py, pz),
            radius: radius * 0.45,
            height: 4.5 * finalScale
          });
        } else if (
          spec.archetype === 'ferrous-spire' ||
          spec.archetype === 'ice-spire'
        ) {
          obstacles.push({
            position: new THREE.Vector3(px, py, pz),
            radius: radius * 0.45,
            height: 3.5 * finalScale
          });
        }

        placed++;
        if (inInner) innerPlaced++;
      }
    }

    // Group transforms by (variant, quadrant). Splitting each variant
    // across 4 spatial quadrants halves the rendered vertex count when
    // the camera looks toward one side of the disc — the away-side
    // quadrants get frustum-culled by their tighter bounding spheres.
    // 4 buckets per variant means ~4× the InstancedMesh count, but
    // draw-call cost stays trivial and the GPU vert throughput drops
    // with view direction.
    const transformsByBucket = new Map<number, THREE.Matrix4[]>();
    for (const t of transforms) {
      // Position lives in matrix elements [12, 13, 14] (x, y, z).
      const px = t.matrix.elements[12]!;
      const pz = t.matrix.elements[14]!;
      const quadrant = (px >= 0 ? 0 : 1) | (pz >= 0 ? 0 : 2);
      const key = t.variantIdx * 4 + quadrant;
      const list = transformsByBucket.get(key);
      if (list) list.push(t.matrix);
      else transformsByBucket.set(key, [t.matrix]);
    }

    for (const [key, matrices] of transformsByBucket) {
      const variantIdx = Math.floor(key / 4);
      const variant = variants[variantIdx];
      if (!variant || matrices.length === 0) continue;
      for (const batch of variant.batches) {
        const inst = new THREE.InstancedMesh(
          batch.geometry,
          batch.material,
          matrices.length
        );
        for (let i = 0; i < matrices.length; i++) {
          inst.setMatrixAt(i, matrices[i]!);
        }
        inst.instanceMatrix.needsUpdate = true;
        inst.frustumCulled = true;
        inst.computeBoundingSphere();
        group.add(inst);
      }
    }
  }

  return { group, obstacles };
}

/** Walk a template Group, bake each sub-mesh's local transform into its
 *  geometry, and merge per-material chunks into a single geometry per
 *  material. Result is ready to feed an `InstancedMesh`. */
function bakeVariant(root: THREE.Object3D): BakedVariant {
  root.updateMatrixWorld(true);
  const rootInv = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const byMaterial = new Map<THREE.Material, THREE.BufferGeometry[]>();
  const localMatrix = new THREE.Matrix4();

  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mat = mesh.material;
    if (Array.isArray(mat) || !mat) return;

    obj.updateMatrixWorld(true);
    localMatrix.multiplyMatrices(rootInv, obj.matrixWorld);

    const geom = mesh.geometry.clone();
    geom.applyMatrix4(localMatrix);
    normalizeForMerge(geom);

    const list = byMaterial.get(mat as THREE.Material);
    if (list) list.push(geom);
    else byMaterial.set(mat as THREE.Material, [geom]);
  });

  const batches: BakedVariant['batches'] = [];
  for (const [material, geoms] of byMaterial) {
    let merged: THREE.BufferGeometry | null;
    if (geoms.length === 1) {
      merged = geoms[0] ?? null;
    } else {
      merged = mergeGeometries(geoms, false);
      for (const g of geoms) g.dispose();
    }
    if (!merged) continue;
    merged.computeBoundingSphere();
    (batches as Array<{ geometry: THREE.BufferGeometry; material: THREE.Material }>).push({
      geometry: merged,
      material
    });
  }

  return { batches };
}

/** mergeGeometries requires every input to have identical attribute keys
 *  and the same indexed-vs-not status. Some of our builders use
 *  position+normal (custom tube) and others position+normal+uv (Lathe,
 *  Plane, Icosahedron). Strip everything to position+normal+index so
 *  they merge cleanly. UVs aren't needed — we don't texture flora. */
function normalizeForMerge(geom: THREE.BufferGeometry): void {
  for (const name of Object.keys(geom.attributes)) {
    if (name !== 'position' && name !== 'normal') {
      geom.deleteAttribute(name);
    }
  }
  if (!geom.attributes['normal']) {
    geom.computeVertexNormals();
  }
  if (!geom.index) {
    const positionAttr = geom.attributes['position'] as THREE.BufferAttribute;
    const count = positionAttr.count;
    const ArrayCtor = count > 65535 ? Uint32Array : Uint16Array;
    const indices = new ArrayCtor(count);
    for (let i = 0; i < count; i++) indices[i] = i;
    geom.setIndex(new THREE.BufferAttribute(indices, 1));
  }
}
